/**
 * 에이전트 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { agentService } from '../../services/agentService';
import { Agent, AgentFilter } from '../../types/agent';

const AgentsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<AgentFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['agents', filter],
    queryFn: () => agentService.getAgents(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<Agent>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'agent_name',
      label: '에이전트명',
      render: (agent) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/agents/${agent.id}`)}
        >
          {agent.agent_name}
        </span>
      ),
    },
    {
      key: 'agent_code',
      label: '에이전트 코드',
      render: (agent) => (
        <span style={{ color: '#ccc' }}>{agent.agent_code}</span>
      ),
    },
    {
      key: 'contact_name',
      label: '담당자',
      render: (agent) => (
        <span style={{ color: '#ccc' }}>{agent.contact_name || '-'}</span>
      ),
    },
    {
      key: 'email',
      label: '이메일',
      render: (agent) => (
        <span style={{ color: '#ccc' }}>{agent.email || '-'}</span>
      ),
    },
    {
      key: 'phone',
      label: '연락처',
      render: (agent) => (
        <span style={{ color: '#ccc' }}>{agent.phone || '-'}</span>
      ),
    },
    {
      key: 'commission_rate',
      label: '수수료율',
      render: (agent) => (
        <span style={{ color: '#00BCD4', fontWeight: 'bold' }}>
          {agent.commission_rate}%
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (agent) => getStatusBadge(agent.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '에이전트명, 코드로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (agent: Agent) => {
    navigate(`/admin/agents/${agent.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>에이전트 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/agents/new')}>
            에이전트 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.agents || []}
              loading={isLoading}
              emptyMessage="에이전트가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AgentsList;

